import React, { useState, useEffect } from 'react';
import { 
  Calendar, RefreshCw, Trash2, Sparkles, Send, Eye, 
  Instagram, Facebook, Video, Image, CheckCircle, 
  Clock, AlertCircle, ChevronLeft, ChevronRight,
  Mail, Users, Filter, Play, Loader2, X, Rocket, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import NewsletterLogs from './NewsletterLogs';

const API = process.env.REACT_APP_BACKEND_URL;

const ContentPlannerManager = () => {
  const [activeTab, setActiveTab] = useState('social');
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Social state
  const [socialPlan, setSocialPlan] = useState({ posts: [], reels: [] });
  const [generatingPlan, setGeneratingPlan] = useState(false);
  
  // Newsletter state
  const [newsletterPlan, setNewsletterPlan] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('all');
  
  // Scheduler status
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Loading states per item
  const [loadingItems, setLoadingItems] = useState({});
  
  // Force publish state
  const [forcePublishing, setForcePublishing] = useState(false);
  
  // Preview modal
  const [previewModal, setPreviewModal] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  
  // Media preview modal (for posts/reels)
  const [mediaPreview, setMediaPreview] = useState(null);
  
  // Helper to build full media URL
  const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    // URL like /api/static/generated/... should become API_URL + /api/static/...
    // API = https://cartella-clinica.preview.emergentagent.com
    return `${API}${url}`;
  };

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadAll();
  }, [currentMonth]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([
      loadSocialPlan(),
      loadNewsletterPlan(),
      loadSchedulerStatus(),
      loadAgeGroups()
    ]);
    setLoading(false);
  };

  const loadSchedulerStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/scheduler/status`);
      setSchedulerStatus(res.data);
    } catch (e) {
      console.error('Error loading scheduler status:', e);
    }
  };

  const loadSocialPlan = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const res = await axios.get(`${API}/api/social/monthly-plan/${year}/${month}`, { headers });
      setSocialPlan(res.data);
    } catch (e) {
      console.error('Error loading social plan:', e);
    }
  };

  const loadNewsletterPlan = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const res = await axios.get(`${API}/api/newsletter-age/plan/${year}/${month}`, { headers });
      setNewsletterPlan(res.data.newsletters || []);
    } catch (e) {
      console.error('Error loading newsletter plan:', e);
    }
  };

  const loadAgeGroups = async () => {
    try {
      const res = await axios.get(`${API}/api/newsletter-age/age-groups`, { headers });
      setAgeGroups(res.data.age_groups || []);
    } catch (e) {
      console.error('Error loading age groups:', e);
    }
  };

  // Generate plan for month
  const generatePlan = async () => {
    if (!window.confirm(`Generare il piano contenuti per ${formatMonthYear(currentMonth)}?\n\nVerranno creati:\n- 3 post/settimana\n- 2 reel/settimana\n- 4 newsletter per fascia d'età`)) return;
    
    setGeneratingPlan(true);
    toast.info('⏳ Generazione piano in corso...');
    
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      
      // Generate social plan
      const socialRes = await axios.post(`${API}/api/social/plan-month`, {
        year, month, posts_per_week: 3, reels_per_week: 2
      }, { headers });
      
      // Generate newsletter plan
      const nlRes = await axios.post(`${API}/api/newsletter-age/plan-month`, {
        year, month, newsletters_per_age_group: 4
      }, { headers });
      
      toast.success(`✅ Piano generato! ${socialRes.data.posts_planned} post, ${socialRes.data.reels_planned} reel, ${nlRes.data.total_planned} newsletter`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione piano');
    } finally {
      setGeneratingPlan(false);
    }
  };

  // Delete content
  const deleteContent = async (contentId, type) => {
    if (!window.confirm('Eliminare questo contenuto?')) return;
    
    setLoadingItems(prev => ({ ...prev, [contentId]: 'deleting' }));
    try {
      if (type === 'social') {
        await axios.delete(`${API}/api/social/content/${contentId}`, { headers });
      } else {
        await axios.delete(`${API}/api/newsletter-age/plan/${contentId}`, { headers });
      }
      toast.success('✅ Contenuto eliminato');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setLoadingItems(prev => ({ ...prev, [contentId]: null }));
    }
  };

  // Generate AI content for social post
  const generateAIContent = async (contentId) => {
    setLoadingItems(prev => ({ ...prev, [contentId]: 'generating' }));
    toast.info('🎨 Generazione AI in corso... (può richiedere fino a 30 secondi)');
    
    try {
      const res = await axios.post(`${API}/api/social/generate-content-with-ai`, 
        { content_id: contentId }, 
        { headers, timeout: 60000 }
      );
      
      if (res.data.success) {
        toast.success('✅ Contenuto AI generato!');
        loadAll();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione AI');
    } finally {
      setLoadingItems(prev => ({ ...prev, [contentId]: null }));
    }
  };

  // Regenerate content with new topic
  const regenerateContent = async (contentId) => {
    setLoadingItems(prev => ({ ...prev, [contentId]: 'regenerating' }));
    toast.info('🔄 Rigenerazione in corso...');
    
    try {
      const res = await axios.post(`${API}/api/social/regenerate/${contentId}`, {}, { headers });
      
      if (res.data.success) {
        toast.success('✅ Contenuto rigenerato con nuovo argomento');
        loadAll();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore rigenerazione');
    } finally {
      setLoadingItems(prev => ({ ...prev, [contentId]: null }));
    }
  };

  // Publish NOW
  const publishNow = async (contentId) => {
    if (!window.confirm('Pubblicare ADESSO questo contenuto?')) return;
    
    setLoadingItems(prev => ({ ...prev, [contentId]: 'publishing' }));
    toast.info('🚀 Pubblicazione in corso...');
    
    try {
      const res = await axios.post(`${API}/api/social/publish-now/${contentId}`, {}, { headers });
      
      if (res.data.success) {
        toast.success('✅ Contenuto pubblicato!');
        loadAll();
      } else {
        toast.error(res.data.message || 'Errore pubblicazione');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore pubblicazione');
    } finally {
      setLoadingItems(prev => ({ ...prev, [contentId]: null }));
    }
  };

  // Preview newsletter
  const previewNewsletter = async (newsletter) => {
    setPreviewModal(newsletter);
    setPreviewLoading(true);
    setPreviewContent(null);
    
    try {
      const res = await axios.post(`${API}/api/newsletter-age/generate-preview`, {
        age_group: newsletter.age_group,
        topic_id: newsletter.topic_id
      }, { headers, timeout: 60000 });
      
      setPreviewContent(res.data);
    } catch (e) {
      toast.error('Errore generazione anteprima');
      setPreviewModal(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Send newsletter
  const sendNewsletter = async (newsletterId, testOnly = false) => {
    const confirmMsg = testOnly 
      ? 'Inviare email di prova al tuo indirizzo?' 
      : 'Inviare questa newsletter a TUTTI i pazienti della fascia d\'età?';
    if (!window.confirm(confirmMsg)) return;
    
    setLoadingItems(prev => ({ ...prev, [newsletterId]: testOnly ? 'testing' : 'sending' }));
    toast.info(testOnly ? '📧 Invio test in corso...' : '📤 Invio newsletter in corso...');
    
    try {
      const res = await axios.post(
        `${API}/api/newsletter-age/send/${newsletterId}?test_only=${testOnly}`, 
        {}, 
        { headers, timeout: 120000 }
      );
      
      if (res.data.success) {
        if (testOnly) {
          toast.success('✅ Email di test inviata!');
        } else {
          toast.success(`✅ Newsletter inviata a ${res.data.sent_count} destinatari!`);
        }
        loadAll();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio newsletter');
    } finally {
      setLoadingItems(prev => ({ ...prev, [newsletterId]: null }));
    }
  };

  // Force publish all pending social posts
  const forcePublishAll = async () => {
    if (!window.confirm('Pubblicare subito TUTTI i post pianificati con data passata?\n\nVerranno pubblicati su Instagram e Facebook.')) return;
    
    setForcePublishing(true);
    toast.info('🚀 Pubblicazione forzata in corso...');
    
    try {
      const res = await axios.post(`${API}/api/scheduler/force-social-publish`, {}, { headers, timeout: 180000 });
      
      if (res.data.success) {
        toast.success('✅ Pubblicazione completata! Controlla i risultati.');
        loadAll();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella pubblicazione forzata');
    } finally {
      setForcePublishing(false);
    }
  };

  // Navigation
  const changeMonth = (delta) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      planned: <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">Pianificato</span>,
      ready: <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Pronto</span>,
      published: <span className="px-2 py-0.5 bg-[#E8F4F8] text-[#2A9FCB] rounded text-xs">Pubblicato</span>,
      sent: <span className="px-2 py-0.5 bg-[#E8F4F8] text-[#2A9FCB] rounded text-xs">Inviata</span>,
      failed: <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Fallito</span>
    };
    return badges[status] || badges.planned;
  };

  // Render loading button
  const renderActionButton = (contentId, action, icon, title, onClick, color = 'gray') => {
    const isLoading = loadingItems[contentId] === action;
    const colorClasses = {
      blue: 'hover:bg-blue-50 text-blue-500',
      green: 'hover:bg-green-50 text-green-500',
      red: 'hover:bg-red-50 text-red-500',
      purple: 'hover:bg-purple-50 text-purple-500',
      orange: 'hover:bg-orange-50 text-orange-500',
      gray: 'hover:bg-gray-50 text-gray-500'
    };
    
    return (
      <button
        onClick={onClick}
        disabled={!!loadingItems[contentId]}
        className={`p-2 rounded-lg transition-all ${colorClasses[color]} disabled:opacity-50`}
        title={title}
        data-testid={`btn-${action}-${contentId}`}
      >
        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-planner-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Piano Contenuti</h2>
          <p className="text-gray-500 text-sm">Gestisci social media e newsletter automatiche</p>
        </div>
        
        {/* Scheduler Status */}
        <div className="flex items-center gap-2">
          {schedulerStatus?.scheduler_running ? (
            <span className="flex items-center gap-1 px-3 py-1 bg-[#E8F4F8] text-[#2A9FCB] rounded-full text-sm">
              <CheckCircle className="w-4 h-4" />
              Scheduler Attivo
            </span>
          ) : (
            <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
              <AlertCircle className="w-4 h-4" />
              Scheduler Offline
            </span>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('social')}
          className={`px-4 py-2 font-medium border-b-2 transition-all ${
            activeTab === 'social' 
              ? 'border-[#2A9FCB] text-[#2A9FCB]' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="planner-tab-social"
        >
          <Instagram className="w-4 h-4 inline mr-2" />
          Social Media
        </button>
        <button
          onClick={() => setActiveTab('newsletter')}
          className={`px-4 py-2 font-medium border-b-2 transition-all ${
            activeTab === 'newsletter' 
              ? 'border-[#2A9FCB] text-[#2A9FCB]' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="planner-tab-newsletter"
        >
          <Mail className="w-4 h-4 inline mr-2" />
          Newsletter
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 font-medium border-b-2 transition-all ${
            activeTab === 'logs' 
              ? 'border-[#2A9FCB] text-[#2A9FCB]' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="planner-tab-logs"
        >
          <BarChart3 className="w-4 h-4 inline mr-2" />
          Log Invii
        </button>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm">
        <button
          onClick={() => changeMonth(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-all"
          data-testid="btn-prev-month"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="text-center">
          <h3 className="text-lg font-semibold capitalize">{formatMonthYear(currentMonth)}</h3>
          <p className="text-sm text-gray-500">
            {activeTab === 'social' 
              ? `${socialPlan.posts?.length || 0} post, ${socialPlan.reels?.length || 0} reel`
              : `${newsletterPlan.length} newsletter`
            }
          </p>
        </div>
        
        <button
          onClick={() => changeMonth(1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-all"
          data-testid="btn-next-month"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Generate Button */}
      <div className="flex gap-3">
        <button
          onClick={generatePlan}
          disabled={generatingPlan}
          className="flex-1 py-3 bg-gradient-to-r from-[#2A9FCB] to-[#1a6b8a] text-white rounded-xl font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="btn-generate-plan"
        >
          {generatingPlan ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Sparkles className="w-5 h-5" />
          )}
          {generatingPlan ? 'Generazione in corso...' : `Genera Piano ${formatMonthYear(currentMonth)}`}
        </button>
        
        {activeTab === 'social' && (
          <button
            onClick={forcePublishAll}
            disabled={forcePublishing}
            className="py-3 px-6 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="btn-force-publish"
          >
            {forcePublishing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Rocket className="w-5 h-5" />
            )}
            {forcePublishing ? 'Pubblicando...' : 'Pubblica Ora'}
          </button>
        )}
      </div>

      {/* Social Media Tab */}
      {activeTab === 'social' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Posts - filtro solo contenuti futuri */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Image className="w-5 h-5 text-pink-500" />
              Post ({socialPlan.posts?.filter(p => new Date(p.scheduled_date) >= new Date(new Date().setHours(0,0,0,0)) || ['failed', 'ready'].includes(p.status)).length || 0})
            </h4>
            
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {socialPlan.posts?.filter(p => new Date(p.scheduled_date) >= new Date(new Date().setHours(0,0,0,0)) || ['failed', 'ready'].includes(p.status)).length === 0 ? (
                <p className="text-gray-400 text-center py-8">Nessun post pianificato per i prossimi giorni</p>
              ) : (
                socialPlan.posts?.filter(p => new Date(p.scheduled_date) >= new Date(new Date().setHours(0,0,0,0)) || ['failed', 'ready'].includes(p.status)).map(post => (
                  <div key={post.id} className="border rounded-lg p-3 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusBadge(post.status)}
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(post.scheduled_date)}
                          </span>
                        </div>
                        <p className="font-medium text-gray-900 truncate">{post.topic_title}</p>
                        {post.caption && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{post.caption}</p>
                        )}
                        {/* Mostra immagine con URL corretto e preview */}
                        {post.image_url ? (
                          <div 
                            className="relative w-16 h-16 mt-2 cursor-pointer group"
                            onClick={() => setMediaPreview({ type: 'image', url: getMediaUrl(post.image_url), title: post.topic_title, caption: post.caption })}
                          >
                            <img src={getMediaUrl(post.image_url)} alt="" className="w-full h-full object-cover rounded" />
                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center rounded">
                              <Eye className="w-5 h-5 text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded mt-2 flex items-center justify-center">
                            <Image className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        {/* Anteprima - sempre visibile se c'è immagine */}
                        {post.image_url && (
                          renderActionButton(post.id, 'preview', <Eye className="w-4 h-4" />, 'Anteprima', () => setMediaPreview({ 
                            type: 'image', 
                            url: getMediaUrl(post.image_url), 
                            title: post.topic_title, 
                            caption: post.caption 
                          }), 'blue')
                        )}
                        {post.status === 'planned' && !post.image_url && (
                          renderActionButton(post.id, 'generating', <Sparkles className="w-4 h-4" />, 'Genera AI', () => generateAIContent(post.id), 'purple')
                        )}
                        {post.image_url && post.status !== 'published' && (
                          renderActionButton(post.id, 'publishing', <Rocket className="w-4 h-4" />, 'Pubblica Ora', () => publishNow(post.id), 'green')
                        )}
                        {renderActionButton(post.id, 'regenerating', <RefreshCw className="w-4 h-4" />, 'Rigenera', () => regenerateContent(post.id), 'blue')}
                        {renderActionButton(post.id, 'deleting', <Trash2 className="w-4 h-4" />, 'Elimina', () => deleteContent(post.id, 'social'), 'red')}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Reels - filtro solo contenuti futuri */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Video className="w-5 h-5 text-purple-500" />
              Reel ({socialPlan.reels?.filter(r => new Date(r.scheduled_date) >= new Date(new Date().setHours(0,0,0,0)) || ['failed', 'ready'].includes(r.status)).length || 0})
            </h4>
            
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {socialPlan.reels?.filter(r => new Date(r.scheduled_date) >= new Date(new Date().setHours(0,0,0,0)) || ['failed', 'ready'].includes(r.status)).length === 0 ? (
                <p className="text-gray-400 text-center py-8">Nessun reel pianificato per i prossimi giorni</p>
              ) : (
                socialPlan.reels?.filter(r => new Date(r.scheduled_date) >= new Date(new Date().setHours(0,0,0,0)) || ['failed', 'ready'].includes(r.status)).map(reel => (
                  <div key={reel.id} className="border rounded-lg p-3 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusBadge(reel.status)}
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(reel.scheduled_date)}
                          </span>
                        </div>
                        <p className="font-medium text-gray-900 truncate">{reel.topic_title}</p>
                        {reel.caption && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{reel.caption}</p>
                        )}
                        {/* Mostra video o immagine con URL corretto */}
                        {reel.video_url ? (
                          <div 
                            className="relative w-16 h-24 mt-2 cursor-pointer group"
                            onClick={() => setMediaPreview({ type: 'video', url: getMediaUrl(reel.video_url), title: reel.topic_title, caption: reel.caption })}
                          >
                            <video 
                              src={getMediaUrl(reel.video_url)} 
                              className="w-full h-full object-cover rounded"
                              muted
                              onMouseEnter={(e) => e.target.play()}
                              onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                            />
                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center rounded">
                              <Play className="w-6 h-6 text-white" />
                            </div>
                          </div>
                        ) : reel.image_url ? (
                          <div 
                            className="relative w-16 h-24 mt-2 cursor-pointer group"
                            onClick={() => setMediaPreview({ type: 'image', url: getMediaUrl(reel.image_url), title: reel.topic_title, caption: reel.caption })}
                          >
                            <img src={getMediaUrl(reel.image_url)} alt="" className="w-full h-full object-cover rounded" />
                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center rounded">
                              <Eye className="w-5 h-5 text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-16 h-24 bg-gray-100 rounded mt-2 flex items-center justify-center">
                            <Play className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        {/* Anteprima - sempre visibile se c'è media */}
                        {(reel.video_url || reel.image_url) && (
                          renderActionButton(reel.id, 'preview', <Eye className="w-4 h-4" />, 'Anteprima', () => setMediaPreview({ 
                            type: reel.video_url ? 'video' : 'image', 
                            url: getMediaUrl(reel.video_url || reel.image_url), 
                            title: reel.topic_title, 
                            caption: reel.caption 
                          }), 'blue')
                        )}
                        {reel.status === 'planned' && !reel.video_url && !reel.image_url && (
                          renderActionButton(reel.id, 'generating', <Sparkles className="w-4 h-4" />, 'Genera Video AI', () => generateAIContent(reel.id), 'purple')
                        )}
                        {(reel.status === 'ready' || ((reel.video_url || reel.image_url) && reel.status !== 'published')) && (
                          renderActionButton(reel.id, 'publishing', <Rocket className="w-4 h-4" />, 'Pubblica Ora', () => publishNow(reel.id), 'green')
                        )}
                        {renderActionButton(reel.id, 'regenerating', <RefreshCw className="w-4 h-4" />, 'Rigenera', () => regenerateContent(reel.id), 'blue')}
                        {renderActionButton(reel.id, 'deleting', <Trash2 className="w-4 h-4" />, 'Elimina', () => deleteContent(reel.id, 'social'), 'red')}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Tab */}
      {activeTab === 'newsletter' && (
        <div className="space-y-4">
          {/* Age Group Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-gray-400" />
            <button
              onClick={() => setSelectedAgeGroup('all')}
              className={`px-3 py-1 rounded-full text-sm transition-all ${
                selectedAgeGroup === 'all' 
                  ? 'bg-[#2A9FCB] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="filter-all"
            >
              Tutte
            </button>
            {ageGroups.map(ag => (
              <button
                key={ag.id}
                onClick={() => setSelectedAgeGroup(ag.id)}
                className={`px-3 py-1 rounded-full text-sm transition-all ${
                  selectedAgeGroup === ag.id 
                    ? 'bg-[#2A9FCB] text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`filter-${ag.id}`}
              >
                {ag.id}
              </button>
            ))}
          </div>

          {/* Newsletter List */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-blue-500" />
              Newsletter per Fascia d&apos;Età
            </h4>
            
            <div className="grid gap-3">
              {newsletterPlan
                .filter(nl => (selectedAgeGroup === 'all' || nl.age_group === selectedAgeGroup) && 
                              (new Date(nl.scheduled_date) >= new Date(new Date().setHours(0,0,0,0)) || nl.status === 'sent'))
                .length === 0 ? (
                <p className="text-gray-400 text-center py-8">Nessuna newsletter pianificata per i prossimi giorni</p>
              ) : (
                newsletterPlan
                  .filter(nl => (selectedAgeGroup === 'all' || nl.age_group === selectedAgeGroup) && 
                                (new Date(nl.scheduled_date) >= new Date(new Date().setHours(0,0,0,0)) || nl.status === 'sent'))
                  .map(nl => (
                    <div key={nl.id} className="border rounded-xl p-4 hover:shadow-sm transition-all">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              {nl.age_group} anni
                            </span>
                            {getStatusBadge(nl.status)}
                          </div>
                          <h5 className="font-medium text-gray-900">{nl.topic_title}</h5>
                          <p className="text-xs text-gray-500 mt-1">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {formatDate(nl.scheduled_date)}
                          </p>
                          {nl.sent_count > 0 && (
                            <p className="text-xs text-[#2A9FCB] mt-1">
                              ✅ Inviate: {nl.sent_count} {nl.failed_count > 0 && `(${nl.failed_count} fallite)`}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {nl.status !== 'sent' && (
                            <>
                              {renderActionButton(nl.id, 'preview', <Eye className="w-4 h-4" />, 'Anteprima', () => previewNewsletter(nl), 'blue')}
                              {renderActionButton(nl.id, 'testing', <Send className="w-4 h-4" />, 'Invia Test', () => sendNewsletter(nl.id, true), 'orange')}
                              {renderActionButton(nl.id, 'sending', <Rocket className="w-4 h-4" />, 'Invia a Tutti', () => sendNewsletter(nl.id, false), 'green')}
                            </>
                          )}
                          {renderActionButton(nl.id, 'deleting', <Trash2 className="w-4 h-4" />, 'Elimina', () => deleteContent(nl.id, 'newsletter'), 'red')}
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
          
          {/* Info Box */}
          <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
            <p className="font-medium mb-2">📅 Automazioni Attive:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Newsletter automatiche inviate alla data pianificata</li>
              <li>Email auguri di compleanno inviate automaticamente ogni mattina alle 8:00</li>
              <li>Post e Reel pubblicati automaticamente alla data programmata</li>
            </ul>
          </div>
        </div>
      )}

      {/* Newsletter Logs Tab */}
      {activeTab === 'logs' && (
        <NewsletterLogs />
      )}

      {/* Preview Modal */}
      {previewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">Anteprima Newsletter</h3>
              <button 
                onClick={() => setPreviewModal(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
                  <span className="ml-3 text-gray-500">Generazione anteprima AI...</span>
                </div>
              ) : previewContent ? (
                <div>
                  <div className="mb-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium mr-2">
                      {previewContent.age_group} anni
                    </span>
                    <span className="text-sm text-gray-500">{previewContent.topic_title}</span>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="font-medium text-gray-900 mb-2">Oggetto: {previewContent.subject}</p>
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: previewContent.content }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
            
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button
                onClick={() => setPreviewModal(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Chiudi
              </button>
              {previewContent && (
                <>
                  <button
                    onClick={() => { sendNewsletter(previewModal.id, true); setPreviewModal(null); }}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  >
                    Invia Test
                  </button>
                  <button
                    onClick={() => { sendNewsletter(previewModal.id, false); setPreviewModal(null); }}
                    className="px-4 py-2 bg-[#2A9FCB] text-white rounded-lg hover:bg-[#1e7ba8]"
                  >
                    Invia a Tutti
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Media Preview Modal (for posts/reels) */}
      {mediaPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setMediaPreview(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-semibold text-gray-900">{mediaPreview.title}</h3>
                <p className="text-xs text-gray-500">{mediaPreview.type === 'video' ? 'Video Reel' : 'Post Immagine'}</p>
              </div>
              <button 
                onClick={() => setMediaPreview(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                data-testid="close-media-preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              {mediaPreview.type === 'video' ? (
                <video
                  src={mediaPreview.url}
                  controls
                  autoPlay
                  className="w-full rounded-lg"
                  style={{ maxHeight: '60vh' }}
                />
              ) : (
                <img 
                  src={mediaPreview.url} 
                  alt={mediaPreview.title} 
                  className="w-full rounded-lg object-contain"
                  style={{ maxHeight: '60vh' }}
                />
              )}
              
              {mediaPreview.caption && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{mediaPreview.caption}</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button
                onClick={() => setMediaPreview(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentPlannerManager;
